import { BaseEntity } from './common';
import { Mention } from './mention';

/**
 * Memory Categories
 * Categories for organizing memories and stories
 */
export enum MemoryCategory {
  CHILDHOOD = 'childhood',
  EDUCATION = 'education',
  CAREER = 'career',
  MILITARY_SERVICE = 'military-service',
  MARRIAGE = 'marriage',
  FAMILY_LIFE = 'family-life',
  HOBBIES = 'hobbies',
  TRAVEL = 'travel',
  ACHIEVEMENTS = 'achievements',
  TRADITIONS = 'traditions',
  RECIPES = 'recipes',
  ADVICE = 'advice',
  FUNNY_STORY = 'funny-story',
  LIFE_LESSON = 'life-lesson',
  HISTORICAL_EVENT = 'historical-event',
  OTHER = 'other'
}

/**
 * Memory approval status
 */
export type MemoryApprovalStatus = 'pending' | 'approved' | 'rejected';

/**
 * Memory entity
 * Represents a story or memory about a person
 */
export interface Memory extends BaseEntity {
  // Association
  personId: string;                    // Person this memory is about
  personName: string;                  // Cached for performance

  // Content
  title: string;                       // Memory title
  content: string;                     // Rich text content (HTML)
  excerpt?: string;                    // Auto-generated or manual excerpt (first 200 chars)

  // Categorization
  category: MemoryCategory;            // Primary category
  tags?: string[];                     // Custom tags

  // Time period
  dateFrom?: string;                   // Start date of memory period (ISO format)
  dateTo?: string;                     // End date of memory period (ISO format)
  timeframe?: string;                  // Human-readable timeframe (e.g., "Summer 1965")

  // Media attachments
  audioURL?: string;                   // Audio recording URL
  audioFileName?: string;              // Original audio filename
  audioSizeMB?: number;                // Audio file size in MB
  audioLengthSeconds?: number;         // Audio length in seconds

  videoURL?: string;                   // Video recording URL
  videoFileName?: string;              // Original video filename
  videoSizeMB?: number;                // Video file size in MB
  videoLengthSeconds?: number;         // Video length in seconds
  videoThumbnailURL?: string;          // Video thumbnail URL

  // Authorship
  authorId: string;                    // Who wrote/recorded the memory
  authorName: string;                  // Cached for performance
  narrator?: string;                   // Who is narrating (if different from author)
  narratorId?: string;                 // Narrator's user ID (if applicable)

  // Publishing
  isDraft: boolean;                    // Draft vs published
  publishedAt?: Date;                  // When published

  // Moderation
  approvalStatus: MemoryApprovalStatus;// Approval status
  isApproved: boolean;                 // Quick check for approved status
  approvedBy?: string;                 // Admin who approved
  approvedAt?: Date;                   // When approved
  rejectionReason?: string;            // Reason for rejection

  // Engagement
  likeCount: number;                   // Number of likes
  viewCount: number;                   // Number of views
  commentCount: number;                // Number of comments

  // Metadata
  lastEditedAt?: Date;                 // Last time content was edited
  autoSavedAt?: Date;                  // Last auto-save timestamp

  // Soft delete
  isDeleted: boolean;                  // Soft delete flag
  deletedBy?: string;                  // Who deleted it
  deletedAt?: Date;                    // When deleted

  // Mentions
  mentions?: Mention[];                // User mentions in memory content
}

/**
 * Memory metadata for creation
 */
export interface MemoryMetadata {
  title: string;
  category: MemoryCategory;
  dateFrom?: string;
  dateTo?: string;
  timeframe?: string;
  tags?: string[];
  narrator?: string;
  narratorId?: string;
}

/**
 * Memory form data
 */
export interface MemoryFormData {
  personId: string;
  title: string;
  content: string;
  category: MemoryCategory;
  dateFrom?: string;
  dateTo?: string;
  timeframe?: string;
  tags?: string[];
  narrator?: string;
  narratorId?: string;
  audioFile?: File;
  videoFile?: File;
  isDraft: boolean;
  mentions?: Mention[];
}

/**
 * Memory filter options
 */
export interface MemoryFilters {
  category?: MemoryCategory;
  authorId?: string;
  dateFrom?: string;
  dateTo?: string;
  tags?: string[];
  isDraft?: boolean;
  approvalStatus?: MemoryApprovalStatus;
  searchQuery?: string;
  hasAudio?: boolean;
  hasVideo?: boolean;
}

/**
 * Memory statistics
 */
export interface MemoryStats {
  totalMemories: number;
  publishedMemories: number;
  draftMemories: number;
  memoriesByCategory: Record<MemoryCategory, number>;
  pendingApproval: number;
  memoriesWithAudio: number;
  memoriesWithVideo: number;
  totalViewCount: number;
  totalLikeCount: number;
}

/**
 * Memory auto-save data
 */
export interface MemoryAutoSaveData {
  memoryId?: string;                   // Existing memory ID (if editing)
  personId: string;
  title: string;
  content: string;
  category: MemoryCategory;
  metadata: Partial<MemoryMetadata>;
  lastSaved: Date;
}
